import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  listOrgLevels,
  createOrgLevel,
  updateOrgLevel,
  deleteOrgLevel,
  type OrgLevelDto,
} from '../../lib/orgLevels';
import { Layers, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/kinerja/org-levels')({
  component: OrgLevelsPage,
});

function OrgLevelsPage() {
  return (
    <ProtectedRoute minimumRole="hr">
      <OrgLevelsContent />
    </ProtectedRoute>
  );
}

function OrgLevelsContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;

  const [orgLevels, setOrgLevels] = useState<OrgLevelDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create form
  const [addingNew, setAddingNew] = useState(false);
  const [newForm, setNewForm] = useState({ level: '', name: '', description: '' });
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ level: '', name: '', description: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    listOrgLevels(companyId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.success && res.data) {
        setOrgLevels(res.data.orgLevels);
      } else {
        setError(res.error ?? 'Failed to load org levels');
      }
    });
    return () => { cancelled = true; };
  }, [companyId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newForm.level || !newForm.name.trim()) return;
    setCreating(true);
    setCreateError('');
    const res = await createOrgLevel(
      {
        companyId,
        level: Number(newForm.level),
        name: newForm.name.trim(),
        description: newForm.description.trim() || undefined,
      },
    );
    setCreating(false);
    if (res.success) {
      toast.success('Org level created');
      setNewForm({ level: '', name: '', description: '' });
      setAddingNew(false);
      load();
    } else {
      setCreateError(res.error ?? 'Failed to create');
      toast.error(res.error ?? 'Failed to create org level');
    }
  }

  function startEdit(ol: OrgLevelDto) {
    setEditingId(ol.id);
    setEditForm({ level: String(ol.level), name: ol.name, description: ol.description ?? '' });
    setEditError('');
  }

  async function handleSaveEdit(id: string) {
    setEditSaving(true);
    setEditError('');
    const res = await updateOrgLevel(
      id,
      {
        companyId,
        level: Number(editForm.level),
        name: editForm.name.trim(),
        description: editForm.description.trim() || undefined,
      },
    );
    setEditSaving(false);
    if (res.success) {
      toast.success('Org level updated');
      setEditingId(null);
      load();
    } else {
      setEditError(res.error ?? 'Failed to save');
      toast.error(res.error ?? 'Failed to update org level');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this org level? This cannot be undone.')) return;
    const res = await deleteOrgLevel(id, companyId);
    if (res.success) toast.success('Org level deleted');
    else toast.error(res.error ?? 'Failed to delete org level');
    load();
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-brand-50 shrink-0">
            <Layers className="w-5 h-5 text-brand-500" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground tracking-tight">Org Levels</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Define the organisational hierarchy levels for your company
            </p>
          </div>
        </div>
        <Button size="sm" onClick={() => setAddingNew(true)}>
          <Plus className="w-4 h-4 mr-1.5" />
          Add Level
        </Button>
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <Card>
        <CardHeader className="py-3 px-4 border-b">
          <CardTitle className="text-sm">Hierarchy Levels</CardTitle>
        </CardHeader>

        <div className="divide-y">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-muted rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              {orgLevels.map((ol) => (
                <div key={ol.id} className="px-4 py-3">
                  {editingId === ol.id ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Level number</Label>
                          <Input
                            type="number"
                            min={1}
                            max={20}
                            className="h-7 text-xs mt-0.5"
                            value={editForm.level}
                            onChange={(e) => setEditForm((f) => ({ ...f, level: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Name</Label>
                          <Input
                            className="h-7 text-xs mt-0.5"
                            value={editForm.name}
                            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Description (optional)</Label>
                        <Input
                          className="h-7 text-xs mt-0.5"
                          value={editForm.description}
                          onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                        />
                      </div>
                      {editError && <p className="text-xs text-destructive">{editError}</p>}
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => handleSaveEdit(ol.id)}
                          disabled={editSaving}
                        >
                          <Check className="w-3.5 h-3.5 mr-1" />
                          {editSaving ? 'Saving…' : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => setEditingId(null)}
                        >
                          <X className="w-3.5 h-3.5 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className="text-xs font-mono w-8 justify-center shrink-0">
                        {ol.level}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{ol.name}</p>
                        {ol.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{ol.description}</p>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={() => startEdit(ol)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(ol.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {orgLevels.length === 0 && !addingNew && (
                <div className="p-8 text-center">
                  <Layers className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No org levels defined yet.</p>
                  <button
                    onClick={() => setAddingNew(true)}
                    className="text-xs text-primary hover:underline mt-1"
                  >
                    Add the first level
                  </button>
                </div>
              )}

              {/* Inline create form */}
              {addingNew && (
                <form onSubmit={handleCreate} className="px-4 py-3 bg-accent/30 space-y-2">
                  <p className="text-xs font-medium">New Org Level</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Level number</Label>
                      <Input
                        type="number"
                        min={1}
                        max={20}
                        className="h-7 text-xs mt-0.5"
                        placeholder="e.g. 7"
                        value={newForm.level}
                        onChange={(e) => setNewForm((f) => ({ ...f, level: e.target.value }))}
                        required
                        autoFocus
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Input
                        className="h-7 text-xs mt-0.5"
                        placeholder="e.g. Principal"
                        value={newForm.name}
                        onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Description (optional)</Label>
                    <Input
                      className="h-7 text-xs mt-0.5"
                      placeholder="Brief description"
                      value={newForm.description}
                      onChange={(e) => setNewForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </div>
                  {createError && <p className="text-xs text-destructive">{createError}</p>}
                  <div className="flex gap-1.5">
                    <Button type="submit" size="sm" className="h-7 text-xs" disabled={creating}>
                      {creating ? 'Creating…' : 'Create Level'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => { setAddingNew(false); setNewForm({ level: '', name: '', description: '' }); setCreateError(''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
